import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from "@angular/core";

import { AuthenticationService } from "../../service/authentication.service";
import { AuthenticationToken } from "../authentication-token.model";
import { BackendPermission } from "../backend-permission.enum";

    /**
     * @description
     *  Directiva estructural que permite renderizar un elemento siempre que
     *  el usuario actual posea los permisos requeridos. Esta directiva
     *  reemplaza el uso de *ngxPermissionsOnly.
     *
     * @example
     *  Un ejemplo de uso para mostrar un botón en el menú:
     *
     *    <button *onlyWith="permission.for('menu:button:Ventas')">
     *      ...
     *
     *  Para usar los permisos en un sentido opuesto (si no posee determinado
     *  permiso), usar:
     *
     *    <button *exceptWith="permission.for('menu:button:Ventas')">
     *      ...
     */

@Directive({ selector: "[exceptWith], [onlyWith]" })
export class OnlyWithDirective implements OnInit {

    /**
     * @description
     *  Token del usuario actualmente autenticado (principal).
     */
	private token: AuthenticationToken = AuthenticationToken.empty();

    constructor(
		private authenticationService: AuthenticationService,
        private template: TemplateRef<any>,
    	private container: ViewContainerRef) {
        this.token = this.authenticationService.token();
    }

    public ngOnInit(): void {
    }

    /**
     * @description
     *  Directiva estructural que habilita un elemento si el principal no
     *  posee ninguno de los permisos especificados.
     *
     * @param permissions
     *  Lista de permisos de backend que el principal no debe poseer.
     */
    @Input()
    public set exceptWith(permissions: BackendPermission[]) {
		if (!this.matchPermission(permissions)) {
            this.container.createEmbeddedView(this.template);
        }
        else {
            this.container.clear();
        }
    }

    /**
     * @description
     *  Directiva estructural que habilita un elemento si el principal posee
     *  alguno de los permisos especificados.
     *
     * @param permissions
     *  Lista de permisos de backend que el principal debería poseer (al menos
     *  debería tener uno de ellos).
     */
    @Input()
    public set onlyWith(permissions: BackendPermission[]) {
		if (this.matchPermission(permissions)) {
            this.container.createEmbeddedView(this.template);
        }
        else {
            this.container.clear();
        }
    }

    /**
     * @description
     *  Determina si el usuario actual posee alguno de los permisos
     *  requeridos, verificando que alguno de ellos se encuentre en su token
     *  de autenticación actual.
     *
     * @param permissions
     *  La lista de permisos solicitados.
     *
     * @returns
     *  Verdadero si el usuario actual posee al menos uno de los permisos
     *  solicitados. Falso, si no posee ninguno de ellos.
     */
    private matchPermission(permissions: BackendPermission[]): boolean {
        return permissions
            .some(permission => this.token.permissions.has(permission));
    }
}
